/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRouteFormatter, getApiKey, getApiVersion, isCloudRunEnabled } from "../ApiWeb";
import { Network } from "./network";
import { UIResourceTypes } from "./ui-resources";

type IApiWorkspace = ApiDataModel.IWorkspace;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentWorkspacesHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedWorkspaceHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onWorkspaceSelected: Function | undefined = undefined;
let _currentWorkspaces: IApiWorkspace[] = [];

export namespace UIWorkspace {
    export function Bind(accessToken: string, onWorkspaceSelected: Function): void {
        _accessToken = accessToken;
        _onWorkspaceSelected = onWorkspaceSelected;

        _rootDiv = _InitializeUI(accessToken);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentWorkspaces = [];

        if (_currentWorkspacesHtmlListElement) {
            _currentWorkspacesHtmlListElement = undefined;
        }

        if (_currentSelectedWorkspaceHtmlDivElement) {
            _currentSelectedWorkspaceHtmlDivElement.style.fontWeight = "";
            _currentSelectedWorkspaceHtmlDivElement.style.textDecoration = "";
        }
    }

    function _InitializeUI(accessToken: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-workspaces-root-div";

        let titleELement = document.createElement("h2");
        titleELement.innerText = "Workspaces";
        rootDiv.appendChild(titleELement);

        // Resources selection div (Model|Quantity|Board)
        rootDiv.appendChild( UIResourceTypes.InitializeUI());
        rootDiv.appendChild(document.createElement("br"));

        // get button
        const getButton: HTMLButtonElement = document.createElement("button");
        getButton.id = "ui-workspaces-get-button";
        getButton.textContent = "Get Workspaces";
        rootDiv.appendChild(getButton);
        getButton.addEventListener("click", async () => {
            _currentWorkspaces = await _FetchWorkspaces(accessToken);
            _currentWorkspacesHtmlListElement = _RefreshUIWorkspaces(_currentWorkspacesHtmlListElement, _currentWorkspaces);
        });

        rootDiv.appendChild(document.createElement("br"));

        // name input
        const nameInput: HTMLInputElement = document.createElement("input");
        nameInput.id = "ui-worksaces-name-input";
        const nameLabelElement = document.createElement("label");
        nameLabelElement.textContent = "Workspace name";
        nameLabelElement.setAttribute("for", nameInput.id);
        rootDiv.appendChild(nameLabelElement);
        rootDiv.appendChild(nameInput);

        // create button
        const createButton: HTMLButtonElement = document.createElement("button");
        createButton.id = "ui-workspaces-create-button";
        createButton.textContent = "Create Workspace";
        rootDiv.appendChild(createButton);
        createButton.addEventListener("click", async () => {
            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
            await _CreateWorkspace(accessToken, sanitizedValue);
            _currentWorkspaces = await _FetchWorkspaces(accessToken);
            _currentWorkspacesHtmlListElement = _RefreshUIWorkspaces(_currentWorkspacesHtmlListElement, _currentWorkspaces);
        });

        // update button
        const updateButton: HTMLButtonElement = document.createElement("button");
        updateButton.id = "ui-workspace-update-button";
        updateButton.textContent = "Update Workspace";
        rootDiv.appendChild(updateButton);
        updateButton.addEventListener("click", async () => {
            if (!_currentSelectedWorkspaceHtmlDivElement) {
                throw new Error("Invalid/Missing current selected workspace");
            }

            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
            const workspace: IApiWorkspace = await _UpdateWorkspace(accessToken, _currentSelectedWorkspaceHtmlDivElement.id, sanitizedValue);
            _UpdateUIWorkspacesFromWorkspace(workspace);
        });

        // list of workspaces
        _currentWorkspacesHtmlListElement = _CreateUIWorkspaces(_currentWorkspaces);
        rootDiv.appendChild(_currentWorkspacesHtmlListElement);

        return rootDiv;
    }


    function _CreateUIWorkspaces(workspaces: IApiWorkspace[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        const sortedWorkspaces: IApiWorkspace[] = workspaces.sort(
            (workspaceA, workspaceB) => new Date(workspaceA.created_at).getTime() - new Date(workspaceB.created_at).getTime()
        );

        for (let workspace of sortedWorkspaces) {
            let cListElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cModelDiv: HTMLDivElement = document.createElement("div");
            cModelDiv.id = workspace.id;
            cModelDiv.textContent = `workspace ${workspace.id} | ${workspace.name} | created at: ${workspace.created_at} | updated at: ${workspace.updated_at}`;
            cListElement.appendChild(cModelDiv);
            cModelDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedWorkspaceHtmlDivElement) {
                    _currentSelectedWorkspaceHtmlDivElement.style.fontWeight = "";
                    _currentSelectedWorkspaceHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cModelDiv.style.fontWeight = "bold";
                cModelDiv.style.textDecoration = "underline";

                // Swap selected workspaces
                _currentSelectedWorkspaceHtmlDivElement = cModelDiv;

                // Call next
                if (_onWorkspaceSelected) {
                    _onWorkspaceSelected(_accessToken, workspace.id, UIResourceTypes.GetSelectedKabResourceType());
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-workspace-get-button-" + workspace.id;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const updatedWorkspace: IApiWorkspace = await _FetchWorkspace(_accessToken, workspace.id);
                _UpdateUIWorkspacesFromWorkspace(updatedWorkspace);
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIWorkspaces(currentList?: HTMLUListElement, workspaces: IApiWorkspace[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIWorkspaces(workspaces);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIWorkspacesFromWorkspace(workspace: IApiWorkspace): void {
        const updatedWorkspaces: IApiWorkspace[] = _currentWorkspaces.map(cWorkspace => (workspace.id === cWorkspace.id ? workspace : cWorkspace));
        _currentWorkspacesHtmlListElement = _RefreshUIWorkspaces(_currentWorkspacesHtmlListElement, updatedWorkspaces);

        if (_currentSelectedWorkspaceHtmlDivElement) {
            _currentSelectedWorkspaceHtmlDivElement.style.fontWeight = "";
            _currentSelectedWorkspaceHtmlDivElement.style.textDecoration = "";
            _currentSelectedWorkspaceHtmlDivElement = undefined;
        }
    }

    async function _FetchWorkspaces(accessToken: string): Promise<IApiWorkspace[]> {
        const apiWorkspacesUrl: string = ApiRouteFormatter.FormatMeWorkspacesUrl(isCloudRunEnabled(), getApiVersion());
        const getMeWorkspacesResponse: { workspaces: IApiWorkspace[] } = await Network.GetMeWorkspaces(apiWorkspacesUrl, getApiKey(), accessToken);
        return getMeWorkspacesResponse.workspaces || [];
    }
    async function _CreateWorkspace(accessToken: string, name: string): Promise<IApiWorkspace> {
        const apiWorkspacesUrl: string = ApiRouteFormatter.FormatMeWorkspacesUrl(isCloudRunEnabled(), getApiVersion());
        const createMeWorkspacesResponse: { workspace: IApiWorkspace } = await Network.PostMeWorkspaces(
            apiWorkspacesUrl,
            getApiKey(),
            accessToken,
            name
        );
        return createMeWorkspacesResponse.workspace;
    }
    async function _FetchWorkspace(accessToken: string, workspaceId: string): Promise<IApiWorkspace> {
        const apiWorkspacesUrl: string = ApiRouteFormatter.FormatMeWorkspaceUrl(isCloudRunEnabled(), getApiVersion(), workspaceId);
        const getMeWorkspaceResponse: { workspace: IApiWorkspace } = await Network.GetMeWorkspace(apiWorkspacesUrl, getApiKey(), accessToken);
        return getMeWorkspaceResponse.workspace;
    }
    async function _UpdateWorkspace(accessToken: string, workspaceId: string, name: string): Promise<IApiWorkspace> {
        const apiWorkspacesUrl: string = ApiRouteFormatter.FormatMeWorkspaceUrl(isCloudRunEnabled(), getApiVersion(), workspaceId);
        const putMeWorkspaceResponse: { workspace: IApiWorkspace } = await Network.PutMeWorkspace(apiWorkspacesUrl, getApiKey(), accessToken, name);
        return putMeWorkspaceResponse.workspace;
    }
}
